import { Faq } from '@/api/gql/graphql';
import {
  useCreateFaq,
  useCreateFaqCategory,
  useDeleteFaq,
  useGetAllFaqCategories,
  useGetFaqByCategory,
  useUpdateFaq,
} from '@/api/hooks/faq';
import { useFaqStore } from '@/store/FaqStore/FaqStore';
import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  FaqByCategoryFormValues,
  FaqFormValues,
  useFaqByCategoryForm,
} from '@/hooks/useFaq';
import { useToast } from '@/hooks/useToast';
import { Options } from '@/components/Common/Selects/Select.types';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';

export const useFaqList = () => {
  const { showToast } = useToast();
  const {
    handleSubmitFaqByCategory,
    registerFaqByCategory,
    errorsFaqByCategory,
    resetFaqByCategory,
    handleSubmitFaq,
    registerFaq,
    errorsFaq,
    resetFaq,
    setValueFaq,
    control,
  } = useFaqByCategoryForm();

  const {
    selectedFaq,
    setSelectedFaq,
    isOpenModal,
    setIsOpenModal,
    isOpenForm,
    setIsOpenForm,
    isEditMode,
    setIsEditMode,
    faq,
    setFaq,
    statusFilter,
    setStatusFilter,
  } = useFaqStore();

  const [category, setCategory] = useState<Options | null>(null);
  const { data: faqCategories, isPending: isLoadingFaqCategories } =
    useGetAllFaqCategories();
  const { data: faqs, isPending: isLoadingFaqs } = useGetFaqByCategory(
    selectedFaq?._id ?? '',
  );
  const [selectedFaqByCategory, setSelectedFaqByCategory] =
    useState<Faq | null>(null);

  const { mutate: createFaqByCategory, isPending: isCreatingFaqByCategory } =
    useCreateFaqCategory();
  const { mutate: createFaq, isPending: isCreatingFaq } = useCreateFaq();
  const { mutate: updateFaq, isPending: isUpdatingFaq } = useUpdateFaq();
  const { mutate: deleteFaq, isPending: isDeletingFaq } = useDeleteFaq();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<string | null>(null);

  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [faqToTogglePublish, setFaqToTogglePublish] = useState<Faq | null>(
    null,
  );

  const [isCreatePublishModalOpen, setIsCreatePublishModalOpen] =
    useState(false);
  const [pendingPublishData, setPendingPublishData] =
    useState<FaqFormValues | null>(null);

  const handleChangeCategory = (value: Options) => {
    setCategory(value);
  };

  const handleCloseForm = () => {
    resetFaq();
    setValueFaq('category', '');
    setIsEditMode(false);
    setIsOpenForm(false);
  };

  const handleCreateFaqByCategory = (data: FaqByCategoryFormValues) => {
    createFaqByCategory(
      {
        name: data.name
      },
      {
        onSuccess: () => {
          resetFaqByCategory();
          setIsOpenModal(false);
        },
        onError: (error) => {
          showToast(error.message, 'error');
        },
      },
    );
  };

  const prepareFaqData = useCallback(
    (data: FaqFormValues, isPublished: boolean) => ({
      category: category?._id,
      answer: data.answer,
      question: data.question,
      isPublished,
    }),
    [category],
  );

  const handleDraftFaq = handleSubmitFaq(async (data: FaqFormValues) => {
    createFaq(prepareFaqData(data, false), {
      onSuccess: () => {
        showToast('Question créée avec succès');
        handleCloseForm();
      },
      onError: (error) => {
        showToast(error.message, 'error');
      },
    });
  });

  const handleDraftUpdateFaq = handleSubmitFaq(async (data: FaqFormValues) => {
    updateFaq(
      {
        faq: faq?._id,
        input: prepareFaqData(data, faq?.isPublished ?? false),
      },
      {
        onSuccess: () => {
          showToast('Question modifiée avec succès');
          handleCloseForm();
        },
        onError: (error) => {
          showToast(error.message, 'error');
        },
      },
    );
  });

  const handleCreateAndPublishFaq = handleSubmitFaq((data: FaqFormValues) => {
    const categoryId = category?._id || (isEditMode && faq?.category?._id);

    if (categoryId) {
      setPendingPublishData({
        ...data,
        category: categoryId,
      });
      setIsCreatePublishModalOpen(true);
    } else {
      showToast('Veuillez sélectionner une catégorie', 'error');
    }
  });

  const handleConfirmCreateAndPublish = () => {
    if (pendingPublishData) {
      if (isEditMode && faq) {
        const categoryId = category?._id || faq?.category?._id;

        updateFaq(
          {
            faq: faq._id,
            input: {
              ...prepareFaqData(pendingPublishData, !faq.isPublished),
              category: categoryId,
            },
          },
          {
            onSuccess: () => {
              showToast(
                faq.isPublished
                  ? 'FAQ masquée avec succès'
                  : 'FAQ publiée avec succès',
              );
              handleCloseForm();
              setIsCreatePublishModalOpen(false);
              setPendingPublishData(null);
            },
            onError: (error) => {
              showToast(error.message, 'error');
            },
          },
        );
      } else {
        createFaq(prepareFaqData(pendingPublishData, true), {
          onSuccess: () => {
            showToast('FAQ publiée avec succès');
            handleCloseForm();
            setIsCreatePublishModalOpen(false);
            setPendingPublishData(null);
          },
          onError: (error) => {
            showToast(error.message, 'error');
          },
        });
      }
    }
  };

  const handleTogglePublish = (faq: Faq) => {
    setFaqToTogglePublish(faq);
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = () => {
    if (faqToTogglePublish) {
      updateFaq(
        {
          faq: faqToTogglePublish._id,
          input: {
            isPublished: !faqToTogglePublish.isPublished,
            answer: faqToTogglePublish.answer,
            category: faqToTogglePublish.category?._id,
            question: faqToTogglePublish.question,
          },
        },
        {
          onSuccess: () => {
            showToast(
              faqToTogglePublish.isPublished
                ? 'Question masquée avec succès'
                : 'Question publiée avec succès',
            );
            setIsPublishModalOpen(false);
            setFaqToTogglePublish(null);
          },
          onError: (error) => {
            showToast(error.message, 'error');
          },
        },
      );
    }
  };

  const handleDeleteFaq = (faqId: string) => {
    setFaqToDelete(faqId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (faqToDelete) {
      deleteFaq(faqToDelete, {
        onSuccess: () => {
          showToast('Question supprimée avec succès');
          setIsDeleteModalOpen(false);
          setFaqToDelete(null);
        },
        onError: (error) => {
          showToast(error.message, 'error');
        },
      });
    }
  };

  const handleEditFaq = (faq: Faq) => {
    setFaq(faq);
    setIsEditMode(true);
    setIsOpenForm(true);
  };

  useEffect(() => {
    if (faqCategories && faqCategories.length > 0 && !selectedFaq) {
      setSelectedFaq(faqCategories[0]);
    }
  }, [faqCategories, selectedFaq]);

  const menuItems = useCallback(
    (faq: Faq) => [
      createMenuItem('Modifier', '', () => handleEditFaq(faq)),
      createMenuItem(`${faq?.isPublished ? 'Cacher' : 'Publier'}`, '', () =>
        handleTogglePublish(faq),
      ),
      createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', () =>
        handleDeleteFaq(faq._id),
      ),
    ],
    [],
  );

  const getPublishButtonText = () => {
    if (isEditMode) {
      return faq?.isPublished ? 'Cacher' : 'Publier';
    }
    return 'Publier';
  };

  const getModalTitle = () => {
    if (isEditMode && faq?.isPublished) {
      return 'Confirmation de masquage';
    }
    return 'Confirmation de publication';
  };

  const getModalMessage = () => {
    if (isEditMode && faq?.isPublished) {
      return 'Êtes-vous sûr de vouloir masquer cette FAQ ?';
    }
    return 'Êtes-vous sûr de vouloir publier cette FAQ ?';
  };

  const openCreateFaq = () => {
    setIsOpenForm(true);
  };

  const handleStatusFilterChange = (values: Options[]) => {
    setStatusFilter(values.map((v) => String(v._id)));
  };

  const filteredFaqs = useMemo(() => {
    if (!faqs) return [];

    if (!statusFilter.length) return faqs;

    return faqs.filter((faq) => {
      const faqStatus = String(faq.isPublished);
      return statusFilter.includes(faqStatus);
    });
  }, [faqs, statusFilter]);

  return {
    selectedFaq,
    setSelectedFaq,
    isOpenModal,
    setIsOpenModal,
    isOpenForm,
    setIsOpenForm,
    faqCategories,
    faqs,
    selectedFaqByCategory,
    setSelectedFaqByCategory,
    handleCreateFaqByCategory,
    handleSubmitFaqByCategory,
    registerFaqByCategory,
    errorsFaqByCategory,
    isCreatingFaqByCategory,
    handleSubmitFaq,
    handleDraftFaq,
    handleDraftUpdateFaq,
    handleCreateAndPublishFaq,
    registerFaq,
    errorsFaq,
    isCreatingFaq,
    setValueFaq,
    handleChangeCategory,
    control,
    menuItems,
    isEditMode,
    setIsEditMode,
    faq,
    setFaq,
    handleCloseForm,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    faqToDelete,
    setFaqToDelete,
    handleConfirmDelete,
    isDeletingFaq,
    isPublishModalOpen,
    setIsPublishModalOpen,
    faqToTogglePublish,
    handleConfirmPublish,
    isUpdatingFaq,
    isCreatePublishModalOpen,
    setIsCreatePublishModalOpen,
    handleConfirmCreateAndPublish,
    getPublishButtonText,
    getModalTitle,
    getModalMessage,
    filteredFaqs,
    openCreateFaq,
    handleStatusFilterChange,
  };
};
